import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as MenuHamburgerIcon } from 'assets/icons/menu-hamburger.svg';
import { ReactComponent as NotificationIcon } from 'assets/icons/notification.svg';
import { Section } from './navbar.interfaces';
import { Roles } from '../../firebase/interfaces';
import { useFetchUserByEmail } from '../../firebase/hooks';
import { useNavbarOptions } from 'hooks/useNavbarOptions';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import {
  NOTIFICATIONS,
  SUPER_ADMIN_ADMINS,
  SUPER_ADMIN_APPOINTMENTS,
  SUPER_ADMIN_COURSES,
  SUPER_ADMIN_LEGAL_DOCS,
  SUPER_ADMIN_PRODUCTS,
} from 'utilities/routes';
import {
  AdminPermissionType,
  useGetFileUrlFromStorageMutation,
  usePrescribeVeradigmMutation,
} from 'graphql/generated/hasura';
import { useAuth } from 'auth/context/AuthContext';
import Avatar from 'components/avatarComponent';
import useDropdown from 'components/dropdown/useDropdown';
import { SuperAdminAccountDropdown } from './NavbarProfileDropdownComponents/SuperAdminAccountDropdown';
import { useCustomToast } from '../../layout/useToast';
import { useModalParams } from '../modal/useModalManager';
import { PasswordChange } from '../../app/my-account/pages/my-profile/components/patientComponents/PasswordChange';
import { useNotificationCount } from '../../notifications/contexts/NotificationsContext';
import { DropdownBodyContainer } from '../dropdown/DropdownBodyContainer';
import { NotificationNavbarDropdown } from '../../app/notifications/components/NotificationaNavbarDropdown/NotificationNavbarDropdown';
import { twMerge } from 'tailwind-merge';
import Dropdown from '../dropdown/dropdownComponent';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import VeradigmModal from '../../app/my-patients/pages/patient/VeradigmModal';
import { EPrescribeSsoMode } from '../../graphql/generated/remote-schema-hasura';
import Spinner from '../Spinner';

interface AuthenticatedNavbarProps {
  openNavbarMobile: () => void;
}

export const SuperAdminAuthenticatedNavbar: React.FC<
  AuthenticatedNavbarProps
> = ({ openNavbarMobile }) => {
  const [isOpenVeradigm, setIsOpenVeradigm] = useState<boolean>(false);

  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.NAVBAR_COMPONENT,
  });
  const navigate = useNavigate();

  const [getToken, { data, loading: loadingToken }] =
    usePrescribeVeradigmMutation({
      variables: { mode: EPrescribeSsoMode.UtilityMode },
      onError: (error) => {
        setIsOpenVeradigm(false);
        addToast({ type: 'warning', message: error.message });
      },
    });

  useEffect(() => {
    if (isOpenVeradigm) {
      getToken();
    }
  }, [isOpenVeradigm, getToken]);

  const {
    loadingOptions,
    AdminProductsOptions,
    AdminProductsRoutes,
    AdminProductsPermissions,
    AdminUsersOptions,
    AdminUsersRoutes,
    AdminUsersPermissions,
  } = useNavbarOptions();

  const { profilePicture, setProfilePicture, user } = useAuth();

  const [getFileUrlFromStorage] = useGetFileUrlFromStorageMutation();
  const { fetchUserByEmailPromise } = useFetchUserByEmail();
  const {
    internalNotificationCount: notificationsCountWithExternalNotifications,
  } = useNotificationCount();

  const userRole = user?.role;
  const [selectedSection, setSelectedSection] = useState<Section>();
  const location = useLocation();
  const firstRendered = useRef(true);

  const { addToast } = useCustomToast();

  const { isOpen: isPasswordChangeModalOpen } =
    useModalParams('password-change');

  const handleUpdatePassword = useCallback(() => {
    addToast({
      type: 'positive',
      message: locale?.passwordChangeSuccess as unknown as string,
    });
  }, [addToast, locale?.passwordChangeSuccess]);

  useEffect(() => {
    const getProfilePicture = async (profilePicture: string) => {
      const image = await getFileUrlFromStorage({
        variables: {
          fileId: profilePicture,
        },
      });
      setProfilePicture(image.data?.GetSignUrlFormStorage?.url || null);
    };
    if (
      (userRole === Roles.PATIENT ||
        userRole === Roles.PROVIDER ||
        userRole === Roles.ADMIN) &&
      user &&
      user.profilePicture &&
      firstRendered.current
    ) {
      getProfilePicture(user.profilePicture);
      firstRendered.current = false;
    }
  }, [
    userRole,
    user,
    getFileUrlFromStorage,
    setProfilePicture,
    fetchUserByEmailPromise,
  ]);

  useEffect(() => {
    const route = location.pathname.split('/')[2];
    setSelectedSection((route as Section) || Section.Dashboard);
  }, [location]);

  const {
    openDropdownId,
    handleDropdownClick,
    registerDropdownRef,
    handleHoverOut,
  } = useDropdown();

  const {
    openDropdownId: isOpenNotificationsDropdown,
    handleDropdownClick: handleNotificationsDropdownClick,
    registerDropdownRef: registerNotificationsDropdownRef,
    handleHoverOut: handleNotificationsDropdownHoverOut,
  } = useDropdown();

  const isSectionSelected = (sections: Array<Section | undefined>): boolean =>
    sections.includes(selectedSection);

  const getFirstRouteByPermissions = (routes: Record<string, string>) => {
    const p = Object.entries(routes).filter(([, perm]) =>
      user?.permissions?.includes(perm),
    );
    return p?.[0]?.[0];
  };

  if ((loading && !locale) || loadingOptions) return null;

  if (isPasswordChangeModalOpen) {
    return <PasswordChange handleUpdatePassword={handleUpdatePassword} />;
  }

  return (
    <div className="flex flex-row h-full">
      <button
        onClick={() => setIsOpenVeradigm(true)}
        className={twMerge(
          'group desktop:flex desktop:flex-col text-med-gray-3 hover:text-white cursor-pointer relative navBarPrimaryNavigationElement',
          userRole !== Roles.SUPERADMIN &&
            !user?.permissions?.includes(AdminPermissionType.VeradigmAdmin) &&
            'desktop:hidden',
        )}
      >
        <div
          className={twMerge(
            'flex h-[84px] group-hover:text-white uppercase font-bold items-center pt-1.5',
          )}
        >
          {loadingToken ? <Spinner className="mx-auto" /> : 'E-PRESCRIBE'}
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
      </button>

      <NavLink
        to={SUPER_ADMIN_COURSES}
        className={twMerge(
          'hidden group desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement',
          userRole !== Roles.SUPERADMIN &&
            !user?.permissions?.includes(AdminPermissionType.ManageEducation) &&
            'desktop:hidden',
        )}
        data-testid={'link-legal-documents'}
      >
        <div
          className={twMerge(
            'flex h-[84px] group-hover:text-white uppercase font-bold items-center pt-1.5',
            isSectionSelected([Section.Courses])
              ? 'text-white'
              : 'text-med-gray-3',
          )}
        >
          <div>{locale?.education ?? 'Education'}</div>
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected([Section.Courses]) ? 'bg-white' : ''
          }`}
        />
      </NavLink>

      <NavLink
        to={SUPER_ADMIN_LEGAL_DOCS}
        className={twMerge(
          'hidden group desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement',
          userRole !== Roles.SUPERADMIN && 'desktop:hidden',
        )}
        data-testid={'link-legal-documents'}
      >
        <div
          className={twMerge(
            'flex h-[84px] group-hover:text-white uppercase font-bold items-center pt-1.5',
            isSectionSelected([Section.LegalDocuments])
              ? 'text-white'
              : 'text-med-gray-3',
          )}
        >
          <div>{locale?.legal ?? 'Legal'}</div>
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected([Section.LegalDocuments]) ? 'bg-white' : ''
          }`}
        />
      </NavLink>

      <NavLink
        to={SUPER_ADMIN_APPOINTMENTS}
        className={twMerge(
          'hidden group desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement',
          userRole !== Roles.SUPERADMIN &&
            !user?.permissions?.includes(
              AdminPermissionType.ViewAppointmentsInfo,
            ) &&
            'desktop:hidden',
        )}
        data-testid={'link-appointments'}
      >
        <div
          className={twMerge(
            'flex h-[84px] group-hover:text-white uppercase font-bold items-center pt-1.5',
            isSectionSelected([Section.AllAppointments])
              ? 'text-white'
              : 'text-med-gray-3',
          )}
        >
          <div>{locale?.appointments ?? 'Appointments'}</div>
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected([Section.AllAppointments]) ? 'bg-white' : ''
          }`}
        />
      </NavLink>

      <div
        className={twMerge(
          'hidden group flex-col cursor-pointer relative navBarPrimaryNavigationElement',
          (user?.role === Roles.SUPERADMIN ||
            !!getFirstRouteByPermissions(AdminUsersPermissions)) &&
            'desktop:flex',
        )}
        onClick={() =>
          user?.role === Roles.SUPERADMIN
            ? navigate(SUPER_ADMIN_ADMINS)
            : navigate(
                AdminUsersRoutes[
                  getFirstRouteByPermissions(AdminUsersPermissions)
                ],
              )
        }
        onMouseEnter={() => handleDropdownClick('users')}
        onMouseLeave={() => handleHoverOut()}
        data-testid="care-history-nav"
      >
        <div ref={(ref) => registerDropdownRef('users', ref)}>
          <div className="flex h-[84px] items-center pt-1.5">
            <div
              className={`text-base uppercase font-bold flex items-center ${
                isSectionSelected([
                  Section.Admin,
                  Section.Patients,
                  Section.Providers,
                ])
                  ? 'text-white'
                  : 'text-med-gray'
              }`}
            >
              <span className="mr-[5px] group-hover:text-white uppercase">
                {AdminUsersOptions[0]}
              </span>
              <ChevronDownIcon className="w-3 h-1.5 fill-current group-hover:fill-white" />
            </div>
          </div>
          <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
          <div
            className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
              isSectionSelected([
                Section.Admin,
                Section.Patients,
                Section.Providers,
              ])
                ? 'bg-white'
                : ''
            }`}
          />
          {openDropdownId === 'users' && (
            <Dropdown
              zIndexClassName="z-[51]"
              options={AdminUsersOptions.filter((opt) => {
                const permission = AdminUsersPermissions[opt];
                return (
                  userRole === Roles.SUPERADMIN ||
                  user?.permissions?.includes(permission)
                );
              })}
              onClick={(event, option) => {
                event.stopPropagation();
                event.preventDefault();
                navigate(AdminUsersRoutes[option]);
              }}
            />
          )}
        </div>
      </div>

      <div
        className={twMerge(
          'hidden group flex-col cursor-pointer relative navBarPrimaryNavigationElement',
          (user?.role === Roles.SUPERADMIN ||
            !!getFirstRouteByPermissions(AdminProductsPermissions)) &&
            'desktop:flex',
        )}
        onClick={() =>
          user?.role === Roles.SUPERADMIN
            ? navigate(SUPER_ADMIN_PRODUCTS)
            : navigate(
                AdminProductsRoutes[
                  getFirstRouteByPermissions(AdminProductsPermissions)
                ],
              )
        }
        onMouseEnter={() => handleDropdownClick('products')}
        onMouseLeave={() => handleHoverOut()}
        data-testid="care-history-nav"
      >
        <div ref={(ref) => registerDropdownRef('products', ref)}>
          <div className="flex h-[84px] items-center pt-1.5">
            <div
              className={`text-base uppercase font-bold flex items-center ${
                isSectionSelected([
                  Section.Products,
                  Section.TestKits,
                  Section.Orders,
                  Section.Payments,
                ])
                  ? 'text-white'
                  : 'text-med-gray'
              }`}
            >
              <span className="mr-[5px] group-hover:text-white uppercase">
                {'Products'}
              </span>
              <ChevronDownIcon className="w-3 h-1.5 fill-current group-hover:fill-white" />
            </div>
          </div>
          <div className="absolute left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
          <div
            className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
              isSectionSelected([
                Section.Products,
                Section.TestKits,
                Section.Orders,
                Section.Payments,
              ])
                ? 'bg-white'
                : ''
            }`}
          />
          {openDropdownId === 'products' && (
            <Dropdown
              zIndexClassName="z-[51]"
              options={AdminProductsOptions.filter((opt) => {
                const permission = AdminProductsPermissions[opt];
                return (
                  userRole === Roles.SUPERADMIN ||
                  user?.permissions?.includes(permission)
                );
              })}
              onClick={(event, option) => {
                event.stopPropagation();
                event.preventDefault();
                navigate(AdminProductsRoutes[option]);
              }}
            />
          )}
        </div>
      </div>

      <div className="hidden desktop:flex desktop:flex-row">
        <div
          className="cursor-pointer relative"
          onClick={() =>
            handleNotificationsDropdownClick('notifications', true)
          }
          data-testid={'link-notifications'}
        >
          <div
            ref={(ref) =>
              registerNotificationsDropdownRef('notifications', ref)
            }
          >
            <div className="flex h-[84px] items-center pt-1.5 px-2.5 relative">
              {notificationsCountWithExternalNotifications > 0 && (
                <div className="absolute top-7 right-2 h-2 w-2 rounded-full bg-orange-body-dot" />
              )}
              <NotificationIcon
                className={`h-6 w-6 ${
                  isSectionSelected([Section.Notifications]) ||
                  isOpenNotificationsDropdown === 'notifications'
                    ? 'fill-white'
                    : 'fill-med-gray-3'
                }`}
              />
            </div>
            <div className="absolute bottom-0 w-full h-[30px] desktop:hover:bg-anav-hover" />
            <div
              className={`flex h-1.5 ${
                isSectionSelected([Section.Notifications]) ? 'bg-white' : ''
              }`}
            />
            {
              <DropdownBodyContainer
                align={'right'}
                className="-right-16 !top-24"
              >
                {isOpenNotificationsDropdown === 'notifications' && (
                  <NotificationNavbarDropdown
                    isOpen
                    onClose={handleNotificationsDropdownHoverOut}
                  />
                )}
              </DropdownBodyContainer>
            }
          </div>
        </div>
      </div>
      <div className="flex flex-col desktop:hidden cursor-pointer relative mr-6">
        <NavLink to={NOTIFICATIONS} className="cursor-pointer relative">
          <div className="flex h-[70px] w-[45px] items-center justify-center">
            {notificationsCountWithExternalNotifications > 0 && (
              <div className="absolute top-5 right-2 h-2 w-2 rounded-full bg-orange-body-dot" />
            )}
            <NotificationIcon
              className={`h-6 w-6 ${
                isSectionSelected([Section.Notifications])
                  ? 'fill-white'
                  : 'fill-med-gray-3'
              }`}
            />
          </div>
          <div
            className={`absolute bottom-0 w-full h-1.5 ${
              isSectionSelected([Section.Notifications]) ? 'bg-white' : ''
            }`}
          />
        </NavLink>
      </div>

      <div className="hidden desktop:flex desktop:flex-row">
        <div
          className="hidden desktop:flex flex-col cursor-pointer relative"
          onClick={() => handleDropdownClick('my-account')}
          onMouseEnter={() => handleDropdownClick('my-account')}
          onMouseLeave={() => handleHoverOut()}
        >
          <div ref={(ref) => registerDropdownRef('my-account', ref)}>
            <div className="flex h-[84px] w-16 items-center justify-center pt-1 px-0">
              <Avatar
                size="sm"
                imageUrl={profilePicture}
                key={profilePicture}
              />
            </div>
            <div className="absolute bottom-0 w-full h-[30px] hover:bg-anav-hover" />
            <div
              className={`flex h-1.5 ${
                isSectionSelected([Section.MyAccount]) ? 'bg-white' : ''
              }`}
            />
            <SuperAdminAccountDropdown
              locale={locale}
              isOpen={openDropdownId === 'my-account'}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col desktop:hidden">
        <div
          className="flex h-[70px] w-[45px] items-center justify-center cursor-pointer"
          onClick={openNavbarMobile}
        >
          <Avatar size="xs" imageUrl={profilePicture} key={profilePicture} />
        </div>
      </div>
      <div className="flex desktop:hidden">
        <div className="flex h-[70px] desktop:h-[84px] w-[45px] desktop:w-16 items-center justify-center desktop:pt-1 desktop:px-0">
          <MenuHamburgerIcon
            className="h-8 w-[45px] cursor-pointer"
            onClick={openNavbarMobile}
          />
        </div>
      </div>

      {data?.ePrescribeJwt?.jwt && (
        <VeradigmModal
          token={data?.ePrescribeJwt?.jwt}
          isOpen={isOpenVeradigm}
          setIsOpen={() => setIsOpenVeradigm(false)}
        />
      )}
    </div>
  );
};
